/**
 * ManageDetailsDialog Component
 * 
 * Dialog for managing account group details (add, edit, remove, reorder).
 */

import React, { useRef } from "react";
import { useKeyboardViewport } from "../../../hooks/useKeyboardViewport";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import CannotChangeLaterIcon from "../../../components/CannotChangeLaterIcon";
import ListItemAutocomplete from "../../../components/ListItemAutocomplete";
import SortableDetailCard from "./SortableDetailCard";
import { Account } from "../../../types";
import { UseAccountSettingsUIStateReturn } from "../hooks/useAccountSettingsUIState";
import { UseAccountSettingsHelpersReturn } from "../hooks/useAccountSettingsHelpers";
import { UseAccountSettingsHandlersReturn } from "../hooks/useAccountSettingsHandlers";
import { useSensors, useSensor, PointerSensor, KeyboardSensor } from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";

export interface ManageDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  uiState: UseAccountSettingsUIStateReturn;
  handlers: UseAccountSettingsHandlersReturn;
  helpers: UseAccountSettingsHelpersReturn;
  accounts: Account[];
}

/**
 * Manage details dialog component
 */
export default function ManageDetailsDialog({
  open,
  onClose,
  uiState,
  handlers,
  helpers,
  accounts,
}: ManageDetailsDialogProps): React.ReactElement {
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const detailTypeSelectRef = useRef<HTMLInputElement | null>(null);
  const { paperRef, viewportStyles } = useKeyboardViewport(open);

  const handleClose = (): void => {
    uiState.setDetailsDialogOpen(false);
    uiState.setEditingGroupDetails([]);
    uiState.setOriginalGroupDetails([]);
    uiState.setEditingGroupName("");
    uiState.setNewDetailName("");
    uiState.setNewDetailType("");
    uiState.setNewDetailDescription("");
    uiState.setNewListItems([]);
    uiState.setDetailNameError("");
    uiState.setEditingDetailIndex(null);
    uiState.setEditingOriginalDetail(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        ref: paperRef,
        sx: {
          maxHeight: "90vh",
          transition: "max-height 0.2s ease-out, margin-top 0.2s ease-out",
          ...viewportStyles,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
          <Typography variant="h6" sx={{ flex: 1, textAlign: "center" }}>
            Manage Details - {uiState.editingGroupName}
          </Typography>
          <IconButton
            sx={{ position: "absolute", right: 0 }} 
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {/* Add/Edit Detail - Always at top */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
              {uiState.editingDetailIndex !== null ? "Edit Detail" : "Add New Detail"}
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Detail Name"
                value={uiState.newDetailName}
                onChange={(e) => {
                  const value = e.target.value;
                  uiState.setNewDetailName(value);
                  // Check for duplicate names (excluding the one being edited)
                  const trimmedName = value.trim();
                  if (trimmedName) {
                    const duplicate = uiState.editingGroupDetails.some(
                      (d, i) => i !== uiState.editingDetailIndex && d.name.toLowerCase() === trimmedName.toLowerCase()
                    );
                    if (duplicate) {
                      uiState.setDetailNameError("A detail with this name already exists.");
                    } else {
                      uiState.setDetailNameError("");
                    }
                  } else {
                    uiState.setDetailNameError("");
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    detailTypeSelectRef.current?.focus();
                  }
                }}
                fullWidth
                placeholder="e.g., Card Number, Account Number"
                error={!!uiState.detailNameError}
                helperText={uiState.detailNameError}
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.75rem",
                  }
                }}
              />
              <FormControl fullWidth required sx={{ position: "relative" }}>
                <InputLabel>Type</InputLabel>
                <Select
                  inputRef={detailTypeSelectRef}
                  value={uiState.newDetailType}
                  label="Type"
                  onChange={(e) => {
                    const newType = e.target.value as "text" | "string" | "number" | "list";
                    uiState.setNewDetailType(newType);
                    uiState.setNewListItems([]); // Clear list items when type changes (but keep if editing)
                  }}
                  disabled={uiState.editingDetailIndex !== null} // Type can't be changed when editing
                  sx={{
                    "& .MuiSelect-select": {
                      pr: 7, // Add padding to make room for icon
                    }
                  }}
                >
                  <MenuItem value="">Select type...</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="string">String</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="list">List</MenuItem>
                </Select>
                <CannotChangeLaterIcon
                  tooltipMessage="Type cannot be changed after creation."
                />
              </FormControl>
              {/* List Items Management - Show when type is "list" */}
              {uiState.newDetailType === "list" && (
                <ListItemAutocomplete
                  items={uiState.newListItems}
                  onChange={uiState.setNewListItems}
                  label="List Items"
                  placeholder="Type to add items (e.g., Bank Name)"
                  detailName={uiState.editingDetailIndex !== null ? uiState.editingGroupDetails[uiState.editingDetailIndex]?.name : undefined}
                  editingGroupName={uiState.editingGroupName}
                  accounts={accounts}
                />
              )}
              <Stack direction="row" spacing={1}>
                {(uiState.editingDetailIndex !== null || (uiState.newDetailName.trim() || uiState.newDetailType)) && (
                  <Button
                    variant="outlined"
                    onClick={handlers.handleCancelEditDetail}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={handlers.handleAddDetailField}
                  disabled={
                    !uiState.newDetailName.trim() || 
                    !uiState.newDetailType || 
                    (uiState.editingDetailIndex !== null && !handlers.hasEditedDetailChanged())
                  }
                  sx={{ flex: 1 }}
                >
                  {uiState.editingDetailIndex !== null ? "Update Detail" : "Add Detail"}
                </Button>
              </Stack>
            </Stack>
          </Box>

          {/* Existing Details List - Below Add Detail section */}
          {uiState.editingGroupDetails.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Existing Details
                </Typography>
                <Box
                  sx={{
                    maxHeight: uiState.editingGroupDetails.length >= 3 ? "240px" : "none",
                    overflowY: uiState.editingGroupDetails.length >= 3 ? "auto" : "visible",
                    overflowX: "hidden",
                    pr: uiState.editingGroupDetails.length >= 3 ? 1 : 0,
                  }}
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handlers.handleDetailDragEnd}
                  >
                    <SortableContext
                      items={uiState.editingGroupDetails.map((_, index) => String(index))}
                      strategy={verticalListSortingStrategy}
                    >
                      <Stack spacing={1}>
                        {uiState.editingGroupDetails.map((detail, index) => (
                          <SortableDetailCard
                            key={index}
                            detail={detail}
                            index={index}
                            isDetailInUse={helpers.isDetailInUse}
                            onEditDetailField={handlers.handleEditDetailField}
                            onRemoveDetailField={handlers.handleRemoveDetailField}
                            editingDetailIndex={uiState.editingDetailIndex}
                          />
                        ))}
                      </Stack>
                    </SortableContext>
                  </DndContext>
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlers.handleSaveDetails}
          disabled={JSON.stringify(uiState.editingGroupDetails) === JSON.stringify(uiState.originalGroupDetails)}
          sx={{ flex: 1, py: 1.5 }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
